import { alpha, Box, Stack, Typography } from '@mui/material';

export const C = {
  accumulation: '#10b981',
  distribution: '#ef4444',
  acceleration: '#3b82f6',
  watch: '#f59e0b',
  bottoming: '#8b5cf6',
  slate: '#64748b',
  sky: '#06b6d4',
  surface: '#ffffff',
  borderSoft: '#e2e8f0',
  border: '#dbe3ee',
  ink: '#1e293b',
  inkMid: '#475569',
  inkSoft: '#94a3b8',
};

export const STATE_FILTERS = [
  { key: 'all', labelKey: 'bottomingFlow.filters.all', color: C.acceleration },
  { key: 'bottoming', labelKey: 'bottomingFlow.filters.bottoming', color: C.bottoming },
  { key: 'accumulating', labelKey: 'bottomingFlow.filters.accumulating', color: C.accumulation },
  { key: 'shakeout', labelKey: 'bottomingFlow.filters.shakeout', color: C.watch },
  { key: 'reclaim', labelKey: 'bottomingFlow.filters.reclaim', color: C.accumulation },
  { key: 'breakdown', labelKey: 'bottomingFlow.filters.breakdown', color: C.distribution },
  { key: 'acceleration', labelKey: 'bottomingFlow.filters.acceleration', color: C.acceleration },
];

export const SCORE_FLOORS = [55, 60, 70];

export const sectionCardSx = {
  borderRadius: 1.5,
  border: `1px solid ${C.borderSoft}`,
  backgroundColor: C.surface,
  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
  overflow: 'hidden',
};

export const sectionHeaderSx = {
  px: 2.5,
  py: 1.55,
  borderBottom: `1px solid ${C.borderSoft}`,
  background: 'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%)',
};

export const tableHeadCellSx = {
  backgroundColor: '#f8fafc',
  borderBottom: `1px solid ${C.borderSoft}`,
  color: '#8392a5',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  py: 1.1,
  px: 2,
  whiteSpace: 'nowrap',
  lineHeight: 1.35,
};

export const tableBodyCellSx = {
  borderBottom: '1px solid #f1f5f9',
  color: C.inkMid,
  fontSize: '0.76rem',
  py: 0.85,
  px: 2,
};

export const monoCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.74rem',
  lineHeight: 1.35,
};

export const candidateColumns = [
  { labelKey: 'bottomingFlow.table.ticker', sx: { width: 180 } },
  { labelKey: 'bottomingFlow.table.sector', sx: { width: 150 } },
  { labelKey: 'bottomingFlow.table.state', sx: { width: 108 } },
  { labelKey: 'bottomingFlow.table.score', sx: { width: 64, textAlign: 'center' } },
  { labelKey: 'bottomingFlow.table.bottomScore', sx: { width: 82, textAlign: 'center' } },
  { labelKey: 'bottomingFlow.table.drySupply', sx: { width: 78, textAlign: 'center' } },
  { labelKey: 'bottomingFlow.table.moneyFlow', sx: { width: 82, textAlign: 'center' } },
  { labelKey: 'bottomingFlow.table.rs20', sx: { width: 78, textAlign: 'center' } },
  { labelKey: 'bottomingFlow.table.shakeout', sx: { width: 104 } },
  { labelKey: 'bottomingFlow.table.price', sx: { width: 92, textAlign: 'right' } },
  { labelKey: 'bottomingFlow.table.base', sx: { width: 105 } },
];

export function toneToColor(tone) {
  const colors = {
    success: C.accumulation,
    danger: C.distribution,
    warning: C.watch,
    info: C.acceleration,
    bottoming: C.bottoming,
    muted: C.slate,
  };
  return colors[tone] || C.slate;
}

export function fmtNum(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function fmtPct(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return `${fmtNum(value, digits)}%`;
}

export function sectorDisplayName(row, lang = 'vi') {
  if (!row) return '-';
  const name = row.sector_name || row.name || row.display_name;
  if (typeof name === 'string') return name;
  if (name?.[lang]) return name[lang];
  if (name?.vi) return name.vi;
  if (name?.en) return name.en;
  return row.sector || '-';
}

function translateRead(t, read, fallback = '-') {
  if (!read?.label_key) return fallback;
  return t(read.label_key, read.detail_params || fallback);
}

function translateDetail(t, read) {
  if (!read?.detail_key) return '';
  return t(read.detail_key, read.detail_params || {});
}

export function MetricCell({ read, t }) {
  const color = toneToColor(read?.tone);
  const label = translateRead(t, read);
  const detail = translateDetail(t, read);
  return (
    <Box sx={{ minWidth: 62, display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography sx={{ px: 0.7, py: 0.2, borderRadius: 0.8, bgcolor: alpha(color, 0.08), fontSize: '0.66rem', fontWeight: 800, color, lineHeight: 1.25 }} noWrap>
        {label}
      </Typography>
      {detail ? <Typography sx={{ mt: 0.25, fontSize: '0.58rem', color: C.inkSoft, lineHeight: 1.2 }} noWrap>{detail}</Typography> : null}
    </Box>
  );
}

export function RelativeStrengthCell({ read, t }) {
  const color = toneToColor(read?.tone);
  return (
    <Box sx={{ minWidth: 62 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color, lineHeight: 1.25 }} noWrap>{translateRead(t, read)}</Typography>
      <Typography sx={{ mt: 0.25, fontSize: '0.62rem', color: C.inkSoft, fontFamily: 'monospace' }}>{fmtPct(read?.value_pct, 1)}</Typography>
    </Box>
  );
}

export function ShakeoutCell({ read, t }) {
  const color = toneToColor(read?.tone);
  const label = read?.label_key ? t(read.label_key, read?.fallback_key ? t(read.fallback_key, '-') : '-') : '-';
  const sub = read?.sub_key ? t(read.sub_key, '') : '';
  return (
    <Box sx={{ minWidth: 76 }}>
      <Typography sx={{ px: 0.7, py: 0.2, borderRadius: 0.8, bgcolor: alpha(color, 0.08), fontSize: '0.66rem', fontWeight: 800, color, lineHeight: 1.25, display: 'inline-flex' }} noWrap>
        {label}
      </Typography>
      {sub ? <Typography sx={{ mt: 0.25, fontSize: '0.58rem', color: C.inkSoft, lineHeight: 1.2 }} noWrap>{sub}</Typography> : null}
    </Box>
  );
}

export function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <Box sx={sectionHeaderSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: alpha(C.acceleration, 0.08), color: C.acceleration }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: C.ink }}>{title}</Typography>
            {subtitle ? (typeof subtitle === 'string' ? <Typography sx={{ mt: 0.15, fontSize: '0.72rem', color: C.inkSoft }}>{subtitle}</Typography> : <Box sx={{ mt: 0.55 }}>{subtitle}</Box>) : null}
          </Box>
        </Box>
        {action || null}
      </Stack>
    </Box>
  );
}

export function HeaderMetric({ label, value, color = C.acceleration }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45, px: 0.75, py: 0.25, borderRadius: 1, bgcolor: alpha(color, 0.08), color }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: C.inkSoft, lineHeight: 1 }}>{label}</Typography>
    </Box>
  );
}

export function FilterPill({ active, label, color, onClick }) {
  return (
    <Box component="button" onClick={onClick} sx={{ height: 28, px: 1, borderRadius: 1, border: 0, bgcolor: active ? alpha(color, 0.1) : 'transparent', color: active ? color : C.inkMid, display: 'inline-flex', alignItems: 'center', gap: 0.55, fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'all 0.15s ease', '&:hover': { bgcolor: alpha(color, active ? 0.12 : 0.06), color } }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: active ? color : alpha(color, 0.45), flexShrink: 0 }} />
      {label}
    </Box>
  );
}

export function ScoreFilterGroup({ scoreFloor, setScoreFloor, strongOnly, setStrongOnly, t }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.45, py: 0.35, bgcolor: '#f8fafc', borderRadius: 1 }}>
      <Typography sx={{ px: 0.45, fontSize: '0.64rem', color: C.inkSoft, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {t('bottomingFlow.filters.score')}
      </Typography>
      {SCORE_FLOORS.map((value) => (
        <Box component="button" key={value} onClick={() => setScoreFloor(value)} sx={{ height: 24, minWidth: 38, px: 0.7, fontSize: '0.66rem', fontWeight: 800, borderRadius: 0.8, border: 0, bgcolor: scoreFloor === value ? C.acceleration : '#fff', color: scoreFloor === value ? '#fff' : C.inkMid, cursor: 'pointer', fontFamily: 'inherit', boxShadow: scoreFloor === value ? `0 1px 5px ${alpha(C.acceleration, 0.22)}` : 'none', '&:hover': { bgcolor: scoreFloor === value ? '#2563eb' : '#f8fafc' } }}>
          ≥ {value}
        </Box>
      ))}
      <Box sx={{ width: 1, height: 18, bgcolor: C.borderSoft, mx: 0.25 }} />
      <Box component="button" onClick={() => setStrongOnly(!strongOnly)} sx={{ height: 24, px: 0.8, borderRadius: 0.8, border: 0, bgcolor: strongOnly ? alpha(C.accumulation, 0.12) : '#fff', color: strongOnly ? C.accumulation : C.inkMid, display: 'inline-flex', alignItems: 'center', gap: 0.45, fontSize: '0.66rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { bgcolor: strongOnly ? alpha(C.accumulation, 0.16) : '#f8fafc' } }}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: strongOnly ? C.accumulation : '#cbd5e1' }} />
        {t('bottomingFlow.filters.strong')}
      </Box>
    </Box>
  );
}

export function BreadthMetric({ label, value, sub, color = C.acceleration }) {
  return (
    <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 0.85, px: 1, py: 0.75, borderRadius: 1, bgcolor: alpha(color, 0.035) }}>
      <Box sx={{ width: 3, alignSelf: 'stretch', minHeight: 36, borderRadius: 1, bgcolor: color, opacity: 0.75, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.6rem', color: C.inkSoft, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 900 }} noWrap>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, mt: 0.1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1rem', color, fontWeight: 950, lineHeight: 1.1, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{value}</Typography>
          {sub ? <Typography sx={{ fontSize: '0.66rem', color: C.inkSoft, lineHeight: 1.25 }} noWrap>{sub}</Typography> : null}
        </Box>
      </Box>
    </Box>
  );
}
